/**
 * Recently Viewed Products Module
 * Handles tracking and displaying recently viewed products
 */

class RecentlyViewed {
  constructor() {
    this.storageKey = 'recently_viewed';
    this.maxProducts = this.getMaxProducts();
    this.init();
  }

  /**
   * Get maximum number of products from data attribute
   */
  getMaxProducts() {
    const element = document.querySelector('recently-viewed');
    return parseInt(element?.getAttribute("data-products-count") || "8", 10);
  }

  /**
   * Escape quotes in strings to prevent JavaScript injection
   */
  escapeQuotes(str) {
    if (typeof str !== 'string') return str;
    return str.replace(/'/g, "\\'").replace(/"/g, '\\"');
  }

  /**
   * Get existing recently viewed data from sessionStorage
   */
  getStoredData() {
    try {
      const data = sessionStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Save data to sessionStorage
   */
  saveData(data) {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      // Silently handle storage errors
    }
  }

  /**
   * Check if product has complete data for rendering
   */
  isValidProduct(product) {
    return product.productTitle && product.productUrl && product.productImg;
  }

  /**
   * Add current product to recently viewed list
   */
  addCurrentProduct() {
    // Check if pdpData is available (defined in recently-viewed.liquid)
    if (typeof pdpData === 'undefined' || !pdpData?.productTitle?.trim()) {
      return;
    }

    const currentProduct = {
      ...pdpData,
      productTitle: this.escapeQuotes(pdpData.productTitle.trim())
    };
    
    const existingData = this.getStoredData();
    
    // Check if product is already in the list
    const isDuplicate = existingData.some(item => item.productTitle === currentProduct.productTitle);
    if (isDuplicate) return;

    // Add new product to the beginning and limit to max products
    const updatedData = [currentProduct, ...existingData].slice(0, this.maxProducts);
    this.saveData(updatedData);
  }

  /**
   * Generate HTML for recently viewed products
   */
  generateProductHTML(product) {
    if (!this.isValidProduct(product)) return '';
    
    const escapedTitle = this.escapeQuotes(product.productTitle.trim());
    return `
      <div class="resource-list__item">
        <product-card class="product-card border-style">
          <a class="product-card__link" href="${product.productUrl}" ref="productCardLink" title="${escapedTitle}">
            <span class="visually-hidden">${escapedTitle}</span>
          </a>
          ${product.productImg}
        </product-card>
      </div>
    `;
  }

  /**
   * Render recently viewed products to the page
   */
  renderProducts() {
    const validProducts = this.getStoredData()
      .filter(product => this.isValidProduct(product))
      .slice(0, this.maxProducts);
    
    if (!validProducts.length) return;

    // Set data-has-recently-viewed to true
    const recentlyViewedElement = document.querySelector('recently-viewed');
    if (recentlyViewedElement) {
      recentlyViewedElement.setAttribute('data-has-recently-viewed', 'true');
    }
    
    // Handle grid layout
    document.querySelectorAll('.js-recentPdpBlockGrid').forEach(element => {
      const html = validProducts.map(product => this.generateProductHTML(product)).join('');
      element.innerHTML = html;
    });

    // Handle carousel layout
    document.querySelectorAll('.js-recentPdpBlockCarousel').forEach(element => {
      const slides = validProducts.map((product, index) => {
        const productHtml = this.generateProductHTML(product);
        return `
          <slideshow-slide
            ref="slides[]"
            aria-hidden="${index === 0 ? 'false' : 'true'}"
            style="view-timeline-name: --slide-${index}; --product-media-fit: cover;"
            slide-id="recently-viewed-${index}"
            class="resource-list__slide"
          >
            ${productHtml}
          </slideshow-slide>
        `;
      }).join('');
      element.innerHTML = slides;
    });
  }

  /**
   * Initialize the recently viewed functionality
   */
  init() {
    this.addCurrentProduct();

    // Render on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.renderProducts());
    } else {
      this.renderProducts();
    }
  }

  /**
   * Static method to get recently viewed products
   * @returns {Array} Array of product IDs
   */
  static getProducts() {
    try {
      const data = sessionStorage.getItem('recently_viewed');
      const products = data ? JSON.parse(data) : [];
      return products.map(product => product.productId || product.id).filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  /**
   * Static method to clear recently viewed products
   */
  static clearProducts() {
    try {
      sessionStorage.setItem('recently_viewed', JSON.stringify([]));
    } catch (error) {
      // Silently handle storage errors
    }
  }

  /**
   * Static method to add a product to recently viewed
   * @param {string} productId - The product ID to add
   */
  static addProduct(productId) {
    if (!productId) return;
    
    try {
      const data = sessionStorage.getItem('recently_viewed');
      const existingData = data ? JSON.parse(data) : [];
      
      // Check if product is already in the list
      const isDuplicate = existingData.some(item => 
        (item.productId || item.id) === productId
      );
      
      if (isDuplicate) return;

      // Only store the product ID - full data will be populated when user visits the product page
      const productData = { productId: productId, id: productId };
      
      // Add new product to the beginning and limit to 8 products
      const updatedData = [productData, ...existingData].slice(0, 8);
      sessionStorage.setItem('recently_viewed', JSON.stringify(updatedData));
    } catch (error) {
      // Silently handle storage errors
    }
  }
}

// Export the class for module imports
export { RecentlyViewed };

// Initialize the module only when not imported as a module
if (typeof window !== 'undefined' && !window.RecentlyViewed) {
  new RecentlyViewed();
}